import { useEffect, useState } from 'react'

const VERSION_URL = '/version.json'
const POLL_INTERVAL = 1000 * 60 * 2 // 2 minutos

/**
 * Pide la versión viva del servidor rompiendo cualquier caché.
 * El query param + cache: 'no-store' garantizan que no nos devuelvan
 * un version.json viejo cacheado (de lo contrario nunca nos enteraríamos).
 */
async function fetchRemoteVersion(signal: AbortSignal): Promise<string | null> {
  try {
    const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
      cache: 'no-store',
      signal
    })
    if (!res.ok) return null
    const data = (await res.json()) as { version?: string }
    return data.version ?? null
  } catch {
    return null
  }
}

/**
 * Detecta si el bundle cargado en el navegador quedó viejo respecto al
 * servidor. Compara la versión horneada en build time (`__APP_VERSION__`)
 * contra la del archivo `/version.json` que se regenera en cada deploy.
 *
 * Dispara la comprobación al montar, al volver el foco a la pestaña y
 * cada 2 minutos como red de seguridad.
 */
export function useVersionCheck(): boolean {
  const [updateAvailable, setUpdateAvailable] = useState(false)

  useEffect(() => {
    // Sin versión local no hay con qué comparar (p.ej. dev sin build).
    if (typeof __APP_VERSION__ === 'undefined') return

    const controller = new AbortController()
    let cancelled = false

    const check = async () => {
      if (cancelled || document.hidden) return
      const remote = await fetchRemoteVersion(controller.signal)
      if (cancelled || remote == null) return
      if (remote !== __APP_VERSION__) setUpdateAvailable(true)
    }

    const interval = setInterval(check, POLL_INTERVAL)
    const onVisibility = () => {
      if (!document.hidden) void check()
    }
    document.addEventListener('visibilitychange', onVisibility)

    void check()

    return () => {
      cancelled = true
      controller.abort()
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [])

  return updateAvailable
}
